import type { When } from "./model.ts";

// Which days of the trip a time covers. Shared by the store (query's `during`) and the web view (the
// Days view), so both place things on the same days. Plain calendar arithmetic, no judgement.
//
// Days are numbered from 1. A trip with dates numbers its days from its first date, so "2026-12-07"
// and "day 10" can be the same day. A trip without dates has no first date: a date then becomes a
// number of days since 1970 (always far above any "day N"), so dates and relative days never collide.

export interface DaySpan {
  first: number;
  last: number;
}

const DAY_MS = 86_400_000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const RELATIVE = /^day ([1-9]\d{0,2})$/;

const epochDay = (date: string) => Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);

// The trip's first date, when it has one.
export function tripStart(dates: When | undefined): string | null {
  if (!dates) return null;
  const first = dates.precision === "days" ? dates.start : dates.precision === "day" ? dates.day : null;
  return first && DATE.test(first) ? first : null;
}

// The day number of a date ("2026-12-07") or a relative day ("day 3").
export function dayNumber(ref: string, start: string | null): number | null {
  const relative = ref.match(RELATIVE);
  if (relative) return Number(relative[1]);
  if (!DATE.test(ref)) return null;
  return start ? epochDay(ref) - epochDay(start) + 1 : epochDay(ref);
}

// The date of a day number, when the trip has a first date.
export function dayDate(n: number, start: string | null): string | null {
  if (!start) return null;
  return new Date((epochDay(start) + n - 1) * DAY_MS).toISOString().slice(0, 10);
}

// The weekday of a day: a date, a local time (its own date, where it happens: times are stored as
// local wall-clock time, so no time zone shifts the day), or "day N" once the trip has a first date.
// Null for a relative day on a trip without dates: there is no weekday to say yet.
export function weekdayOf(ref: string, start: string | null): Weekday | null {
  const date = resolveDate(ref, start);
  return date ? (WEEKDAYS[new Date(epochDay(date) * DAY_MS).getUTCDay()] ?? null) : null;
}

// The weekdays a time falls on, each end in its own calendar: "Sun", or "Sat to Mon" when it starts and
// ends on different days (a span of days, a night train). Null when it has no day, or a day without a
// date yet.
export function whenWeekday(when: When | undefined, start: string | null): string | null {
  if (!when) return null;
  switch (when.precision) {
    case "none":
      return null;
    case "day":
      return weekdayOf(when.day, start);
    case "part":
      return when.day === undefined ? null : weekdayOf(when.day, start);
    case "days":
    case "window":
    case "exact": {
      const first = resolveDate(when.start, start);
      const last = when.end === undefined ? first : resolveDate(when.end, start);
      if (!first || !last) return null;
      const a = weekdayOf(first, null);
      const b = weekdayOf(last, null);
      if (!a || !b) return null;
      return first === last ? a : `${a} to ${b}`;
    }
  }
}

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

// The date a day stands for: a date or a local time's date as written, "day N" counted from the trip's
// first date. Null when it has none.
function resolveDate(ref: string, start: string | null): string | null {
  const relative = ref.match(RELATIVE);
  if (relative) return dayDate(Number(relative[1]), start);
  const date = ref.slice(0, 10);
  return DATE.test(date) ? date : null;
}

// The days a time covers; null when it has no day (none, or a part of the day without one).
export function daySpan(when: When | undefined, start: string | null): DaySpan | null {
  if (!when) return null;
  const one = (ref: string | undefined): DaySpan | null => {
    const n = ref === undefined ? null : dayNumber(ref, start);
    return n === null ? null : { first: n, last: n };
  };
  switch (when.precision) {
    case "none":
      return null;
    case "day":
      return one(when.day);
    case "part":
      return one(when.day);
    case "days": {
      const first = dayNumber(when.start, start);
      const last = dayNumber(when.end, start);
      return first === null || last === null ? null : { first: Math.min(first, last), last: Math.max(first, last) };
    }
    case "window":
    case "exact": {
      const first = dayNumber(when.start.slice(0, 10), start);
      const end = when.end ? dayNumber(when.end.slice(0, 10), start) : first;
      return first === null ? null : { first, last: Math.max(first, end ?? first) };
    }
  }
}

export function overlaps(a: DaySpan, b: DaySpan): boolean {
  return a.first <= b.last && b.first <= a.last;
}

// The date booking opens for something: its `books_from` date, or that many days before its first day.
// Null when it doesn't say, or its first day has no date.
export function bookingOpens(
  booksFrom: string | { days_before: number } | undefined,
  when: When | undefined,
  start: string | null,
): string | null {
  if (!booksFrom) return null;
  if (typeof booksFrom === "string") return booksFrom;
  const span = daySpan(when, start);
  if (!span) return null;
  const first = start ? dayDate(span.first, start) : span.first > 10_000 ? fromEpochDay(span.first) : null;
  return first ? fromEpochDay(epochDay(first) - booksFrom.days_before) : null;
}

const fromEpochDay = (n: number) => new Date(n * DAY_MS).toISOString().slice(0, 10);

// When people can share where they are with a trip: from the day before its first date to the day
// after its last, as dates. Null for a trip without dates.
export function locationWindow(dates: When | undefined): { opens: string; closes: string } | null {
  const first = tripStart(dates);
  if (!first || !dates) return null;
  const last = dates.precision === "days" && DATE.test(dates.end) ? dates.end : first;
  return { opens: fromEpochDay(epochDay(first) - 1), closes: fromEpochDay(epochDay(last) + 1) };
}

// Whether an instant falls inside a window of dates (both ends whole days, in any time zone on Earth:
// a day either side of UTC, so nobody's local date is cut off).
export function inWindow(window: { opens: string; closes: string } | null, now: Date): boolean {
  if (!window) return false;
  const day = now.toISOString().slice(0, 10);
  return day >= window.opens && day <= window.closes;
}
